/* ═══════════════════════════════════════════════════════════
   VPDOT MOTION SYSTEM  —  vpdot-motion.js
   Premium animation engine. Zero dependencies. Vanilla JS.
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════
     2. SCROLL PROGRESS BAR
  ═══════════════════════════════════════════ */
  function initProgress() {
    const bar = document.createElement('div');
    bar.id = 'vp-progress';
    document.body.appendChild(bar);

    function update() {
      const scrolled    = window.scrollY;
      const docHeight   = document.documentElement.scrollHeight - window.innerHeight;
      const pct         = docHeight > 0 ? (scrolled / docHeight) * 100 : 0;
      bar.style.width   = Math.min(pct, 100) + '%';
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ═══════════════════════════════════════════
     3. PAGE TRANSITION
     Wipe overlay on navigation. Enters from bottom,
     exits upward revealing the new page.
  ═══════════════════════════════════════════ */
  function initPageTransition() {
    const overlay = document.createElement('div');
    overlay.id = 'vp-page-transition';
    document.body.appendChild(overlay);
    // Overlay starts at opacity:0 (invisible) — page is always visible on load.
    // On navigation: fades to black, then redirects.

    // Fix BFCache: remove overlay if page is loaded from cache (e.g. Back button)
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        overlay.classList.remove('is-entering');
      }
    });

    document.addEventListener('click', e => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;
      if (href.startsWith('#') || href.startsWith('javascript:') || href === '') return;
      if (link.getAttribute('target') === '_blank') return;
      if (link.getAttribute('download') !== null) return;

      // Only intercept same-origin links
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.hostname !== window.location.hostname) return;
        if (url.href === window.location.href) return;
      } catch (err) { return; }

      e.preventDefault();
      const dest = link.href;

      // Force reflow before adding class so transition fires
      void overlay.offsetHeight;
      overlay.classList.add('is-entering');

      setTimeout(() => {
        window.location.href = dest;
      }, 460);
    });
  }

  /* ═══════════════════════════════════════════
     4. ENHANCED SCROLL REVEAL
     Replaces the basic observer in theme.liquid.
     Supports all reveal-* variants + stagger-children.
  ═══════════════════════════════════════════ */
  function initReveal() {
    const selector = [
      '.reveal',
      '.reveal-left',
      '.reveal-right',
      '.reveal-scale',
      '.reveal-blur',
      '.reveal-clip',
      '.stagger-children',
    ].join(', ');

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('active');
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -5% 0px',
      threshold: 0.06,
    });

    document.querySelectorAll(selector).forEach(el => observer.observe(el));
  }

  /* ═══════════════════════════════════════════
     5. HERO ENTRANCE
     Fires on DOMContentLoaded. Triggers in-order
     for left panel, then right panel with slight offset.
  ═══════════════════════════════════════════ */
  function initHeroEntrance() {
    // Only arm the hidden entrance states when the page reached us quickly.
    // On slow connections the hero text must stay visible from first paint —
    // hiding it until this JS runs was pushing mobile LCP to 5s+.
    if (performance.now() < 1500) {
      document.documentElement.classList.add('vp-motion-armed');
    }

    const leftPanel  = document.querySelector('#hero-panel-left .hero-dual__content');
    const rightPanel = document.querySelector('#hero-panel-right .hero-dual__content');

    function revealPanel(panel, baseDelay) {
      if (!panel) return;
      const lines = panel.querySelectorAll('.vp-hero-line');
      const fades = panel.querySelectorAll('.vp-hero-fade');

      lines.forEach((line, i) => {
        setTimeout(() => line.classList.add('is-visible'), baseDelay + i * 210);
      });

      const afterLines = baseDelay + lines.length * 210;
      fades.forEach((el, i) => {
        setTimeout(() => el.classList.add('is-visible'), afterLines + i * 130);
      });
    }

    // Also handle single-panel hero (hero.liquid)
    const singleHero = document.querySelector('.hero .hero__content');
    if (singleHero) {
      revealPanel(singleHero, 160);
    }

    revealPanel(leftPanel, 180);
    revealPanel(rightPanel, 360); // right panel starts 180ms after left
  }

  /* ═══════════════════════════════════════════
     6. MAGNETIC BUTTONS
     Subtle pull toward cursor on hover.
  ═══════════════════════════════════════════ */
  function initMagnetic() {
    const selector = '.hero-btn, .hero-dual__btn, .btn--primary';

    document.querySelectorAll(selector).forEach(btn => {
      btn.addEventListener('mousemove', e => {
        const rect = btn.getBoundingClientRect();
        const x    = (e.clientX - rect.left - rect.width  / 2) * 0.20;
        const y    = (e.clientY - rect.top  - rect.height / 2) * 0.20;
        btn.style.transition = 'transform 0.12s ease';
        btn.style.transform  = `translate(${x}px, ${y}px)`;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transition = 'transform 0.65s cubic-bezier(0.16, 1, 0.3, 1)';
        btn.style.transform  = '';
      });
    });
  }

  /* ═══════════════════════════════════════════
     7. NUMBER COUNTERS
     Animate numbers up when they enter the viewport.
     Usage: <span class="vp-counter" data-target="10000" data-suffix="+"></span>
  ═══════════════════════════════════════════ */
  function initCounters() {
    const counters = document.querySelectorAll('.vp-counter[data-target]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el       = entry.target;
        const target   = parseFloat(el.dataset.target);
        const duration = parseInt(el.dataset.duration || '1800', 10);
        const suffix   = el.dataset.suffix || '';
        const decimals = el.dataset.decimals ? parseInt(el.dataset.decimals, 10) : 0;
        const start    = performance.now();

        const tick = now => {
          const elapsed  = now - start;
          const progress = Math.min(elapsed / duration, 1);
          // Ease-out cubic
          const eased    = 1 - Math.pow(1 - progress, 3);
          const current  = (target * eased).toFixed(decimals);
          el.textContent = current + suffix;
          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
        observer.unobserve(el);
      });
    }, { threshold: 0.5 });

    counters.forEach(c => observer.observe(c));
  }

  /* ═══════════════════════════════════════════
     8. LAZY IMAGE FADE-IN
     Smooth opacity transition when lazy images load.
  ═══════════════════════════════════════════ */
  function initLazyImages() {
    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
      }
    });
  }

  /* ═══════════════════════════════════════════
     9. SUBTLE PARALLAX
     Add data-speed="0.1" to any .vp-parallax element.
     Lower = slower = more dramatic depth effect.
  ═══════════════════════════════════════════ */
  function initParallax() {
    const els = document.querySelectorAll('.vp-parallax');
    if (!els.length) return;

    // Skip on mobile — parallax can cause jank on low-power devices
    if (window.matchMedia('(max-width: 768px)').matches) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        els.forEach(el => {
          const rect  = el.getBoundingClientRect();
          const speed = parseFloat(el.dataset.speed || '0.12');
          const mid   = rect.top + rect.height / 2 - window.innerHeight / 2;
          el.style.transform = `translateY(${mid * speed}px)`;
        });
        ticking = false;
      });
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════
     10. HERO VIDEO PARALLAX
     Slowly scale the hero background on scroll
     for a depth-of-field effect.
  ═══════════════════════════════════════════ */
  function initHeroParallax() {
    const videos = document.querySelectorAll('.hero__video-bg, .hero-dual__slide video');
    if (!videos.length) return;
    if (window.matchMedia('(max-width: 768px)').matches) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const pct     = Math.min(scrollY / window.innerHeight, 1);
        const scale   = 1 + pct * 0.06;          // max 6% scale growth
        const opacity = Math.max(0.35, 0.5 - pct * 0.15);

        videos.forEach(v => {
          v.style.transform = `translate(-50%, -50%) scale(${scale * 1.15})`;
          v.style.opacity   = opacity;
        });
        ticking = false;
      });
    }, { passive: true });
  }

  /* ═══════════════════════════════════════════
     INIT — runs on DOMContentLoaded
  ═══════════════════════════════════════════ */
  function init() {
    initProgress();
    // initPageTransition() removed: it held every internal link click for 460ms before navigating
    initReveal();
    initHeroEntrance();
    initMagnetic();
    initCounters();
    initLazyImages();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
